import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { TabLayout } from "@/components/Layout/BaseLayouts/TabLayout";
import {
  FriendsGroupCard,
  FriendsGroupStatus,
} from "@/components/Portal/FriendsWithBenefits/Cards/FriendsGroupCard.tsx";
import { FriendsMembersCard } from "@/components/Portal/FriendsWithBenefits/Cards/FriendsMembersCard.tsx";
import { FriendsRatesCard } from "@/components/Portal/FriendsWithBenefits/Cards/FriendsRatesCard.tsx";
import { InviteFriendsCard } from "@/components/Portal/FriendsWithBenefits/Cards/InviteFriendsCard.tsx";
import { FriendsWithBenefitsHeader } from "@/components/Portal/FriendsWithBenefits/FriendsWithBenefitsHeader.tsx";
import { HowItWorksModal } from "@/components/Portal/FriendsWithBenefits/Modals/HowItWorksModal.tsx";
import { LeaveGroupModal } from "@/components/Portal/FriendsWithBenefits/Modals/LeaveGroupModal.tsx";
import useNoEligibleProductsYet from "@/hooks/useNoEligibleProductsYet";
import { portalRoutes } from "@/router/routes/portalRoutes";
import {
  useGetCustomer,
  useGetCustomerTribe,
  useGetDetailTribe,
  useGetInvitationLink,
  useLeaveTribe,
} from "@/services/api";

/** The first discount level needs two eligible products, every later one just one. */
const FIRST_LEVEL_MISSING_PRODUCTS = 2;
const FIRST_LEVEL_DISCOUNT = 5;

const GroupOverview = () => {
  const { t } = useTranslation("portal", {
    keyPrefix: "friendsWithBenefits.groupOverview",
  });
  const navigate = useNavigate();
  const [showLeaveGroupModal, setShowLeaveGroupModal] = useState(false);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);

  const { data: customer } = useGetCustomer();
  const { data: customerTribe } = useGetCustomerTribe();
  const { data: tribeDetail } = useGetDetailTribe(customerTribe?.tribeId || "");
  const { data: invitationLink } = useGetInvitationLink({
    query: {
      queryKey: ["invitationLink", customerTribe?.tribeId],
      enabled: !!customerTribe?.tribeId,
    },
  });

  const { mutate: leaveTribe, isLoading: isLeavingGroup } = useLeaveTribe();
  const noEligibleProductsYet = useNoEligibleProductsYet();

  const members = tribeDetail?.memberInfo || [];
  const isGroupFull = !!tribeDetail?.maxMembersReached;
  const discount = tribeDetail?.tribeDiscount ?? 0;
  const nextDiscount = tribeDetail?.tribeDiscountNext ?? 0;
  const isMaxDiscountReached = nextDiscount <= discount;

  const currentMember = members.find(
    (member) =>
      member.userId !== undefined &&
      String(member.userId) === String(customer?.customerId),
  );
  // Without an own Flat Mobile the discount status is replaced with the
  // rates section, so the customer can book one straight away.
  const hasOwnFlatMobile = (currentMember?.eligibleSims ?? 0) > 0;

  const groupStatusByState: Record<string, FriendsGroupStatus> = {
    "full-max": "fullMaxDiscount",
    "full-upsell": "full",
    "open-max": "maxDiscount",
    "open-upsell": "upsell",
  };
  // Until the detail has been loaded, the fallbacks would read as "maximum discount"
  // and "no own Flat Mobile", which is the wrong status and the wrong section.
  const isGroupLoaded = !!tribeDetail;
  const groupStatus: FriendsGroupStatus =
    isGroupLoaded && hasOwnFlatMobile
      ? groupStatusByState[
          `${isGroupFull ? "full" : "open"}-${isMaxDiscountReached ? "max" : "upsell"}`
        ]
      : "none";

  const ownerName = customer?.firstName;
  // The possessive form belongs to the language, not to the component. While
  // the customer is still being loaded, there is no name to possess, so the plain
  // heading is shown instead of "'s Friends with Benefits".
  const groupTitle = !ownerName
    ? t("friendsGroup")
    : ownerName.endsWith("s")
      ? t("groupTitleSEnding", { name: ownerName })
      : t("groupTitle", { name: ownerName });

  const confirmLeaveGroup = () => {
    leaveTribe(undefined, {
      onSuccess: () => {
        setShowLeaveGroupModal(false);
        navigate(portalRoutes.friendsWithBenefits.createGroup);
      },
    });
  };

  return (
    <TabLayout header={<FriendsWithBenefitsHeader />}>
      <div className="flex flex-col gap-gutter">
        <FriendsGroupCard
          dataTestid="fwb-group-card"
          title={groupTitle}
          status={groupStatus}
          discount={discount}
          nextDiscount={
            noEligibleProductsYet ? FIRST_LEVEL_DISCOUNT : nextDiscount
          }
          missingForNextLevel={
            noEligibleProductsYet ? FIRST_LEVEL_MISSING_PRODUCTS : 1
          }
          onShowInstructions={() => setShowInstructionsModal(true)}
          onLeaveGroup={() => setShowLeaveGroupModal(true)}
        />
        {isGroupLoaded && !hasOwnFlatMobile && (
          <FriendsRatesCard
            dataTestid="fwb-rates-card"
            discount={nextDiscount}
            onOrderFlatMobile={() => navigate(portalRoutes.addDevice.step1)}
          />
        )}
        <InviteFriendsCard
          dataTestid="fwb-invite-card"
          invitationLink={invitationLink || ""}
          disabled={isGroupFull}
        />
        <FriendsMembersCard
          dataTestid="fwb-members-card"
          members={members}
          currentUserId={customer?.customerId}
          discount={discount}
          memberCount={tribeDetail?.membersCounter ?? 0}
          eligibleSimCount={tribeDetail?.eligibleSimCount ?? 0}
        />
      </div>
      {showLeaveGroupModal && (
        <LeaveGroupModal
          isConfirming={isLeavingGroup}
          onConfirm={confirmLeaveGroup}
          onClose={() => setShowLeaveGroupModal(false)}
        />
      )}
      {showInstructionsModal && (
        <HowItWorksModal onClose={() => setShowInstructionsModal(false)} />
      )}
    </TabLayout>
  );
};

export default GroupOverview;
